import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useStore, useApiStore } from '@core/store';
import { PersistKeys } from '@core/store/slices/persistenceSlice';

type PreselectStrategy = 'first' | 'all';

export interface PersistedState {
    selectedShopIds: string[];
    setSelectedShopIds: (ids: string[]) => void;
}

export interface ShopPersistedOptions {
    preselect: PreselectStrategy;
    inheritsPersisted: boolean;
    /**
     * Internal — set to true by usePersistence when the shop key is not
     * included in the options object. Allows the hook to always be called
     * (rules of hooks) while still returning inert state when not needed.
     */
    skip?: boolean;
}

function useShopPersistenceInternal(options: ShopPersistedOptions): PersistedState {
    const getPersisted = useStore((s) => s.getPersisted);
    const setPersisted = useStore((s) => s.setPersisted);

    const { shopIds } = useApiStore();
    const shopItemIds = useMemo(() => shopIds.map((s) => s.id), [shopIds]);

    const [selectedShopIds, setSelectedShopIdsLocal] = useState<string[]>([]);
    const hasUserInteracted = useRef(false);
    const prevIdsRef = useRef<string[]>([]);

    useEffect(() => {
        if (options.skip) return;

        if (shopItemIds.length === 0) {
            hasUserInteracted.current = false;
            return;
        }

        if (hasUserInteracted.current) return;

        let seeded: string[] = [];

        if (options.inheritsPersisted) {
            const persisted = getPersisted(PersistKeys.SHOP_IDS);
            if (persisted && persisted.length > 0) {
                const valid = persisted.filter((id) => shopItemIds.includes(id));
                if (valid.length > 0) seeded = valid;
            }
        }

        if (seeded.length === 0) {
            seeded = options.preselect === 'all' ? [...shopItemIds] : shopItemIds[0] ? [shopItemIds[0]] : [];
        }

        const changed =
            seeded.length !== prevIdsRef.current.length || seeded.some((id, i) => id !== prevIdsRef.current[i]);

        if (changed) {
            prevIdsRef.current = seeded;
            setSelectedShopIdsLocal(seeded);
        }
    }, [shopItemIds, options.skip, options.inheritsPersisted, options.preselect, getPersisted]);

    const setSelectedShopIds = useCallback(
        (ids: string[]) => {
            if (options.skip) return;
            hasUserInteracted.current = true;
            prevIdsRef.current = ids;
            setSelectedShopIdsLocal(ids);
            setPersisted(PersistKeys.SHOP_IDS, ids);
        },
        [options.skip, setPersisted]
    );

    return { selectedShopIds, setSelectedShopIds };
}

/**
 * Options for usePersistence.
 * Each key is optional — only include the keys your page needs.
 * All per-concept hooks are always called internally (rules of hooks safe).
 */
export interface UsePersistenceOptions {
    shop?: ShopPersistedOptions;
}

export interface PersistenceResult {
    shop: PersistedState;
}

export function usePersistence(options: UsePersistenceOptions): PersistenceResult {
    const shop = useShopPersistenceInternal(
        options.shop ?? { preselect: 'first', inheritsPersisted: false, skip: true }
    );

    return { shop };
}

/**
 * Standard single-shop persistence.
 * Inherits the cross-page shop. Falls back to the first available shop.
 * Use on any page with a single-shop selector.
 *
 * Derive the scalar: const shopId = selectedShopIds[0] ?? null
 */
export const useShopPersistence = () => usePersistence({ shop: { preselect: 'first', inheritsPersisted: true } }).shop;

/**
 * Multi-shop persistence.
 * Inherits the cross-page shop selection. Falls back to all available shops.
 * Use on pages with an AllShopsSelector.
 */
export const useMultiShopPersistence = () =>
    usePersistence({ shop: { preselect: 'all', inheritsPersisted: true } }).shop;

/**
 * My Shops variant — always defaults to all shops, never inherits.
 * Use only on My Shops (or equivalent landing pages).
 * Still writes back on change so navigating away persists the selection.
 */
export const useMyShopsPersistence = () =>
    usePersistence({ shop: { preselect: 'all', inheritsPersisted: false } }).shop;
